/**
 * Device registration and cache downloads.
 *
 * uc-core endpoints (F4.1-d, F4.1-f, F5 store cache); the shapes are validated with zod so a
 * mismatch fails loudly at the boundary.
 *   GET  /devices/me                     → DeviceRegistration (device identified by X-Device-Uid)
 *   GET  /devices/me/roster              → RosterSnapshot   (village devices)
 *   GET  /sites/{site_id}/store-cache    → StoreCache       (store devices; village: its collection point)
 */
import { z } from 'zod'
import { addHeaderProvider, api, isApiError } from '@/api'
import { getDb, type SupplyChainDB } from './db'
import { getMeta, getOrCreateDeviceUid, setMeta, type DeviceRegistration } from './meta'
import { rosterSnapshotSchema, saveRoster, wipeRoster } from './roster'
import { resealLocalData } from './sealed'
import { readStoreCache, saveStoreCache, storeCacheSchema } from './storeCache'
import {
  packRunSchema,
  patchSitePack,
  readSitePack,
  saveSitePack,
  sitePackSchema,
} from './sitePack'

export const DEVICE_UID_HEADER = 'X-Device-Uid'

const teamSchema = z.looseObject({
  kind: z.enum(['VILLAGE', 'SITE']),
  id: z.string(),
  name: z.string(),
  code: z.string(),
})

/** uc-core GET /devices/me: `{ device (entity, camelCase), status, wipe, server_time, team }`. */
const registrationSchema = z.looseObject({
  device: z.looseObject({
    id: z.string(),
    deviceUid: z.string(),
    codePrefix: z.string(),
    registeredAt: z.string().nullish(),
    group: z.looseObject({ id: z.string(), name: z.string().nullish() }).nullish(),
    location: z
      .looseObject({ id: z.string(), name: z.string().nullish(), code: z.string().nullish() })
      .nullish(),
  }),
  status: z.enum(['ACTIVE', 'REVOKED']),
  team: teamSchema.nullish(),
})

/** The server's answer → what the phone keeps. The team falls back to the device's village/site. */
export function toRegistration(body: z.infer<typeof registrationSchema>): DeviceRegistration {
  const d = body.device
  const team =
    body.team ??
    (d.group
      ? { kind: 'VILLAGE' as const, id: d.group.id, name: d.group.name ?? '', code: '' }
      : d.location
        ? {
            kind: 'SITE' as const,
            id: d.location.id,
            name: d.location.name ?? '',
            code: d.location.code ?? '',
          }
        : null)
  if (!team) throw new Error('This phone is registered without a village or site')
  return {
    id: d.id,
    device_uid: d.deviceUid,
    code_prefix: d.codePrefix,
    team,
    status: body.status,
    registered_at: d.registeredAt ?? new Date().toISOString(),
  }
}

let uidCache: string | null = null

/**
 * A phone set up with "Load demo data" (offline/devSeed.ts): every demo team id starts with
 * `dev-`. The server has never heard of it, so its lists cannot be downloaded or checked.
 */
export function isDemoDevice(device: Pick<DeviceRegistration, 'team'> | null | undefined): boolean {
  return !!device && device.team.id.startsWith('dev-')
}

/** "Update list" on a demo phone: its lists come from the demo data, not the server. */
export class DemoDeviceError extends Error {
  constructor() {
    super('Demo phone: its lists come from the demo data, not the server')
    this.name = 'DemoDeviceError'
  }
}

/** Call once at start-up: every API request then carries the phone's device uid. */
export async function initDeviceIdentity(db: SupplyChainDB = getDb()): Promise<string> {
  uidCache = await getOrCreateDeviceUid(db)
  addHeaderProvider((): Record<string, string> =>
    uidCache ? { [DEVICE_UID_HEADER]: uidCache } : {},
  )
  return uidCache
}

/**
 * This phone's registration. A phone not yet set up is registered by the server to the signed-in
 * person's village; `groupId` names the village when that is not clear (office, or several).
 */
export async function refreshRegistration(
  db: SupplyChainDB = getDb(),
  groupId?: string,
): Promise<DeviceRegistration> {
  // The server would answer "not registered" and wipe the demo phone's registration.
  const local = await getMeta('device', db)
  if (local && isDemoDevice(local)) throw new DemoDeviceError()
  const reg = toRegistration(
    await api.get('/devices/me', {
      schema: registrationSchema,
      ...(groupId ? { query: { groupId } } : {}),
    }),
  )
  await setMeta('device', reg, db)
  if (reg.status === 'REVOKED') await wipeDevice(db)
  return reg
}

/**
 * "Update farmer list": download the roster (village) or the site cache (store). Multi-village
 * phones (V1): `pick` moves the phone to another of the operator's villages and downloads its list.
 */
export async function downloadCaches(
  db: SupplyChainDB = getDb(),
  pick?: { groupId: string },
): Promise<void> {
  const device = await getMeta('device', db)
  if (!device) throw new Error('This phone is not registered yet')
  if (isDemoDevice(device)) throw new DemoDeviceError()
  if (device.team.kind === 'VILLAGE') {
    const query = pick ? `?groupId=${encodeURIComponent(pick.groupId)}` : ''
    const snapshot = await api.get(`/devices/me/roster${query}`, {
      schema: rosterSnapshotSchema,
      timeoutMs: 60_000,
    })
    const { keyChanged } = await saveRoster(snapshot, db)
    // The phone's village as the server has it now (it moves when she picks another village).
    const v = snapshot.village
    const villageCode = v?.code?.trim() || device.team.code
    if (
      v &&
      (v.group_id !== device.team.id ||
        (v.name && v.name !== device.team.name) ||
        villageCode !== device.team.code)
    ) {
      await setMeta(
        'device',
        {
          ...device,
          team: {
            ...device.team,
            id: v.group_id,
            name: v.name ?? device.team.name,
            code: villageCode,
          },
        },
        db,
      )
    }
    // The key arrived or changed: seal what was stored plain, or with the old key (R10).
    if (keyChanged) await resealLocalData(db)
    // F5.1: the village phone loads vehicles from its collection point's store cache.
    const cpId = snapshot.config['collection_point_id']
    if (typeof cpId === 'string' && cpId) {
      const cache = await api.get(`/sites/${encodeURIComponent(cpId)}/store-cache`, {
        schema: storeCacheSchema,
        timeoutMs: 60_000,
      })
      await saveStoreCache(cache, db, Date.now(), false)
    }
  } else {
    const cache = await api.get(`/sites/${encodeURIComponent(device.team.id)}/store-cache`, {
      schema: storeCacheSchema,
      timeoutMs: 60_000,
    })
    await saveStoreCache(cache, db)
    await downloadSitePack(device.team.id, db)
  }
}

/**
 * Processing site or packing hall: GET /process-runs/site-pack (machines, settings, runs, issue
 * tasks, pack runs …), then each started pack run's device bundle. A site with no machines and no
 * pack line answers 404 — that is not an error, the phone just has no process or pack screens.
 */
export async function downloadSitePack(
  siteId: string,
  db: SupplyChainDB = getDb(),
): Promise<boolean> {
  try {
    const pack = await api.get('/process-runs/site-pack', {
      query: { locationId: siteId },
      schema: sitePackSchema,
      timeoutMs: 60_000,
    })
    await saveSitePack(pack, db)
  } catch (e) {
    if (isApiError(e) && e.kind === 'not_found') return false
    throw e
  }
  const stored = await readSitePack(db)
  for (const run of stored?.pack.pack_runs ?? []) {
    if (run.started_at && !['CLOSED', 'BALANCED', 'EXCEPTION', 'CANCELLED'].includes(run.status))
      await downloadPackBundle(run.id, db).catch(() => undefined)
  }
  return true
}

/** F7.3-e: the pack run's offline bundle (lot code, QR link, label lines, sacks issued …). */
export async function downloadPackBundle(
  runId: string,
  db: SupplyChainDB = getDb(),
): Promise<void> {
  const bundle = await api.get(`/pack-runs/${encodeURIComponent(runId)}/device-bundle`, {
    schema: packRunSchema,
    timeoutMs: 60_000,
  })
  const at = new Date().toISOString()
  await patchSitePack(
    (p) => ({
      ...p,
      pack_runs: p.pack_runs.some((r) => r.id === runId)
        ? p.pack_runs.map((r) => (r.id === runId ? { ...bundle, bundle_at: at } : r))
        : [...p.pack_runs, { ...bundle, bundle_at: at }],
    }),
    db,
  )
}

/** Re-download the site pack this phone keeps, after commands were delivered. */
export async function refreshSitePack(db: SupplyChainDB = getDb()): Promise<boolean> {
  const device = await getMeta('device', db)
  if (!device || device.status !== 'ACTIVE' || device.team.kind !== 'SITE') return false
  if (isDemoDevice(device) || !(await readSitePack(db))) return false
  return downloadSitePack(device.team.id, db)
}

/**
 * Re-download only the store list this phone already keeps (its site, or a village phone's
 * collection point), so bags committed or received since show up for loading and putting away.
 * Quiet no-op when the phone has no store list or no registration.
 */
export async function refreshStoreCache(db: SupplyChainDB = getDb()): Promise<boolean> {
  const device = await getMeta('device', db)
  if (!device || device.status !== 'ACTIVE' || isDemoDevice(device)) return false
  const current = await readStoreCache(db)
  if (!current) return false
  const cache = await api.get(`/sites/${encodeURIComponent(current.cache.site_id)}/store-cache`, {
    schema: storeCacheSchema,
    timeoutMs: 60_000,
  })
  await saveStoreCache(cache, db, Date.now(), device.team.kind !== 'VILLAGE')
  return true
}

/**
 * Revoked by the office: wipe the roster, PIN checks and vault key. Unsent commands and
 * photos are KEPT — the server applies them and holds the lots (F4.1 validations). Their
 * payloads are sealed with the vault key, so the key stays until the last of them is out:
 * every sync reply on a revoked phone calls this again, and the reply that settles the last
 * command drops the key. Drafts are not kept readable for that (they become unreadable).
 */
export async function wipeDevice(db: SupplyChainDB = getDb()): Promise<void> {
  const unsent = await db.outbox
    .where('status')
    // ERROR and SET_ASIDE too: a supervisor may still put them back or send them; dropping the
    // key would leave their sealed field data unreadable for good.
    .anyOf(['PENDING', 'SENDING', 'WAIT_FOR_PARENT', 'ERROR', 'SET_ASIDE'])
    .count()
  await wipeRoster(db, { keepKey: unsent > 0 })
  const reg = await getMeta('device', db)
  if (reg) await setMeta('device', { ...reg, status: 'REVOKED' }, db)
}
